import type { ProviderAccountTestResult } from '@/api/schemas';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';

const ICON: Record<string, { icon: string; tone: string }> = {
  passed: { icon: 'check_circle', tone: 'text-succeeded' },
  failed: { icon: 'error', tone: 'text-failed' },
  error: { icon: 'error', tone: 'text-failed' },
  warning: { icon: 'warning', tone: 'text-awaiting' },
  skipped: { icon: 'do_not_disturb_on', tone: 'text-subtle' },
};

/** Why a check did not pass, when the API says so (`details.reason`, e.g. "no credential attached"). */
const reasonOf = (details: Record<string, unknown> | undefined): string | undefined => (typeof details?.reason === 'string' ? details.reason : undefined);

function ResultHeading({ result, accountName }: { result: ProviderAccountTestResult; accountName: string }) {
  return (
    <p className="mb-1.5 flex flex-wrap items-center gap-x-1.5 font-medium text-foreground">
      <Icon name={result.ok ? 'check_circle' : 'error'} size={14} className={result.ok ? 'text-succeeded' : 'text-failed'} />
      {accountName}: {result.ok ? 'connection OK' : 'connection failed'}
      <span className="font-mono font-normal text-subtle">· {formatDateTime(result.checkedAt)}</span>
    </p>
  );
}

function CheckRow({ check }: { check: ProviderAccountTestResult['checks'][number] }) {
  const look = ICON[check.status] ?? ICON.warning!;
  const reason = check.status !== 'passed' ? reasonOf(check.details) : undefined;
  return (
    <li data-check={check.key} data-status={check.status} className="flex items-start gap-1.5">
      <Icon name={look.icon} size={14} className={cn('mt-px shrink-0', look.tone)} />
      <span className="min-w-0 text-muted-foreground">
        <code className="mr-1 font-mono text-[11px] text-foreground">{check.key}</code>
        {check.message}
        {reason ? <span className="block text-subtle">{reason}</span> : null}
      </span>
    </li>
  );
}

function PlanLine({ result }: { result: ProviderAccountTestResult }) {
  return (
    <li className="text-subtle">
      plan {result.account?.plan ?? '—'} · {result.discoveredConcurrency} concurrent · {result.latencyMs ?? '—'} ms
    </li>
  );
}

/**
 * The checks of "Test connection" (`POST /provider-accounts/{id}/test`): credential, connectivity and account, each
 * with its status, message and, when it did not pass, the reason the API gives.
 */
export function TestResultChecks({ result, accountName, className }: { result: ProviderAccountTestResult; accountName?: string; className?: string }) {
  return (
    <div data-testid="provider-account-test-result" data-ok={result.ok} className={cn('rounded-md border bg-card p-2.5 text-xs', className)}>
      {accountName ? <ResultHeading result={result} accountName={accountName} /> : null}
      <ul className="flex flex-col gap-1">
        {result.checks.map((c) => (
          <CheckRow key={c.key} check={c} />
        ))}
        {result.discoveredConcurrency ? <PlanLine result={result} /> : null}
      </ul>
    </div>
  );
}
